<script setup>
/**
 * ONE PAGE OF THE FORM, on the Arrange form page (services spec P-4.10 design 9): its title,
 * its questions with their controls, and its button words.
 *
 * It moves nothing itself. Every control emits, and the page applies the move
 * (`helpers/form-layout.js`) and asks the server for a new preview, so there is one place a
 * layout changes and one place it is drawn from.
 *
 * The LAST page's button is the task's own submit words (`ctaEn`/`ctaAr`, the same words the
 * Check screen edits); every other page's is its Next words. Both are one line on WhatsApp's
 * Footer, capped at 35.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import BilingualBox from '../task/BilingualBox.vue';
import WasloSelect from '../WasloSelect.vue';
import { useWasloI18n, wasloLocaleTable } from '../../i18n.js';
import { toIntlLocale } from '../../helpers/intl.js';
import {
  MAX_FORM_BUTTON,
  MAX_FORM_TITLE,
  questionWords,
} from '../../helpers/form-layout.js';

const props = defineProps({
  /** `{ titleEn?, titleAr?, nextEn?, nextAr?, fields: [{ step, optional? }] }`. */
  page: { type: Object, required: true },
  index: { type: Number, required: true },
  total: { type: Number, required: true },
  /** The task's steps by id, for each question's own words. */
  steps: { type: Object, default: () => ({}) },
  /** The language the owner reads her questions in. */
  lang: { type: String, default: 'en' },
  /** "Your customers write in": `ar`, `en` or `both`. */
  languages: { type: String, default: 'both' },
  /** The task's submit words, which the LAST page's button edits. */
  cta: { type: Object, default: () => ({ en: '', ar: '' }) },
  /** Sentences about this page (no question on it, words too long). */
  errors: { type: Array, default: () => [] },
  /** A sentence per step id (the deciding question not last, a question twice). */
  stepErrors: { type: Object, default: () => ({}) },
  canRemove: { type: Boolean, default: false },
});

const emit = defineEmits([
  'up',
  'down',
  'move',
  'optional',
  'chat',
  'words',
  'cta',
  'remove',
]);

const { t, locale } = useWasloI18n();
const key = name => `WASLO.FORMS.ARRANGE.${name}`;
const format = computed(
  () => new Intl.NumberFormat(toIntlLocale(locale.value))
);

/** Waslo's own Next words in each language: what the form says when she writes none. */
const NEXT_WORDS = {
  en: wasloLocaleTable('en').WASLO.FORMS.ARRANGE.NEXT_DEFAULT,
  ar: wasloLocaleTable('ar').WASLO.FORMS.ARRANGE.NEXT_DEFAULT,
};

const isLast = computed(() => props.index === props.total - 1);
const heading = computed(() =>
  t(key('PAGE'), { n: format.value.format(props.index + 1) })
);
const pageOptions = computed(() =>
  Array.from({ length: props.total }, (_, at) => ({
    value: String(at),
    label: t(key('PAGE'), { n: format.value.format(at + 1) }),
  }))
);
const wordsOf = stepId =>
  questionWords(props.steps[stepId], props.lang) || stepId;
</script>

<template>
  <li
    class="flex flex-col gap-4 p-4 list-none rounded-xl outline outline-1 -outline-offset-1 outline-n-container bg-n-solid-1"
    :data-waslo-form-page="index"
  >
    <div class="flex items-center justify-between gap-2">
      <p class="mb-0 text-sm font-medium text-n-slate-12">{{ heading }}</p>
      <Button
        v-if="canRemove"
        :label="t(key('REMOVE_PAGE'))"
        variant="faded"
        color="slate"
        size="sm"
        data-waslo-form-page-remove
        @click="emit('remove')"
      />
    </div>

    <p
      v-for="error in errors"
      :key="error"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-form-page-error
    >
      {{ error }}
    </p>

    <BilingualBox
      :en="page.titleEn ?? ''"
      :ar="page.titleAr ?? ''"
      :label="t(key('PAGE_TITLE'))"
      :max-length="MAX_FORM_TITLE"
      kind="task_question"
      :languages="languages"
      data-waslo-form-page-title
      @update:en="value => emit('words', { titleEn: value })"
      @update:ar="value => emit('words', { titleAr: value })"
      @update:pair="
        pair => emit('words', { titleEn: pair.en, titleAr: pair.ar })
      "
    />

    <ol class="flex flex-col gap-3 mb-0 list-none">
      <li
        v-for="(field, at) in page.fields"
        :key="field.step"
        class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-1"
        :data-waslo-form-question="field.step"
      >
        <div class="flex flex-wrap items-center gap-2">
          <bdi class="flex-1 text-sm text-n-slate-12" dir="auto">
            {{ wordsOf(field.step) }}
          </bdi>
          <Button
            icon="i-lucide-arrow-up"
            variant="ghost"
            color="slate"
            size="sm"
            :aria-label="t(key('MOVE_UP'))"
            :disabled="at === 0"
            data-waslo-form-question-up
            @click="emit('up', field.step)"
          />
          <Button
            icon="i-lucide-arrow-down"
            variant="ghost"
            color="slate"
            size="sm"
            :aria-label="t(key('MOVE_DOWN'))"
            :disabled="at === page.fields.length - 1"
            data-waslo-form-question-down
            @click="emit('down', field.step)"
          />
        </div>
        <div class="flex flex-wrap items-center gap-4">
          <WasloSelect
            v-if="total > 1"
            :model-value="String(index)"
            :label="t(key('MOVE_TO'))"
            :options="pageOptions"
            data-waslo-form-question-move
            @update:model-value="
              value => emit('move', field.step, Number(value))
            "
          />
          <label class="flex items-center gap-2 mb-0 text-sm text-n-slate-11">
            <Switch
              :model-value="field.optional === true"
              data-waslo-form-question-skip
              @update:model-value="value => emit('optional', field.step, value)"
            />
            {{ t(key('SKIPPABLE')) }}
          </label>
          <button
            type="button"
            class="text-sm underline text-start text-n-slate-12"
            data-waslo-form-question-chat
            @click="emit('chat', field.step)"
          >
            {{ t(key('TO_CHAT')) }}
          </button>
        </div>
        <p v-if="field.optional" class="mb-0 text-xs text-n-slate-10">
          {{ t(key('SKIP_NOTE')) }}
        </p>
        <p
          v-if="stepErrors[field.step]"
          class="mb-0 text-sm text-n-ruby-9"
          data-waslo-form-question-error
        >
          {{ stepErrors[field.step] }}
        </p>
      </li>
    </ol>

    <BilingualBox
      v-if="isLast"
      :en="cta.en"
      :ar="cta.ar"
      :label="t(key('BUTTON'))"
      :max-length="MAX_FORM_BUTTON"
      kind="task_cta"
      :languages="languages"
      data-waslo-form-page-button
      @update:en="value => emit('cta', { en: value })"
      @update:ar="value => emit('cta', { ar: value })"
      @update:pair="pair => emit('cta', { en: pair.en, ar: pair.ar })"
    />
    <BilingualBox
      v-else
      :en="page.nextEn ?? ''"
      :ar="page.nextAr ?? ''"
      :label="t(key('BUTTON'))"
      :placeholder-en="NEXT_WORDS.en"
      :placeholder-ar="NEXT_WORDS.ar"
      :max-length="MAX_FORM_BUTTON"
      kind="task_cta"
      :languages="languages"
      data-waslo-form-page-button
      @update:en="value => emit('words', { nextEn: value })"
      @update:ar="value => emit('words', { nextAr: value })"
      @update:pair="pair => emit('words', { nextEn: pair.en, nextAr: pair.ar })"
    />
  </li>
</template>
